import React, { useState } from "react";
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Tooltip, Typography } from "@mui/material";

import { LayerIds } from "fond/types";
import { ConfirmModal } from "fond/widgets";

import { getUnmetDataPrepRequirements } from "./dataPrepRequirements";
import { DataPrepGenerateOptions } from "./dataPrepRunParameters";

interface RunDataPrepProps {
  /**
   * Layers present in the version, used to check the generate options' requirements and detect data that generating would replace
   */
  layerKeys: string[];
  /**
   * Whether every precondition for running data prep other than the layer requirements is met
   */
  canRun: boolean;
  readOnly: boolean;
  generate: DataPrepGenerateOptions;
  onGenerateChange: (generate: DataPrepGenerateOptions) => void;
  onRun: () => void;
}

const RunDataPrep: React.FC<RunDataPrepProps> = ({ layerKeys, canRun, readOnly, generate, onGenerateChange, onRun }: RunDataPrepProps) => {
  const [isConfirmingReplace, setIsConfirmingReplace] = useState<boolean>(false);

  const noGenerationSelected = !generate.underground && !generate.leadins;
  const existingUnderground = layerKeys.includes(LayerIds.inStreet) || layerKeys.includes(LayerIds.inStreetPoints);
  const existingLeadins = layerKeys.includes(LayerIds.inLeadIns);
  const replacesUnderground = generate.underground && existingUnderground;
  const replacesLeadins = generate.leadins && existingLeadins;
  const replacesExistingData = replacesUnderground || replacesLeadins;

  const replacedLayerNames: string[] = [];
  if (replacesUnderground) replacedLayerNames.push("underground path and points");
  if (replacesLeadins) replacedLayerNames.push("leadins");

  const unmetRequirements = getUnmetDataPrepRequirements(layerKeys, generate);
  const isDisabled = !canRun || noGenerationSelected || readOnly || unmetRequirements.length > 0;

  const handleRun = () => {
    if (replacesExistingData) {
      setIsConfirmingReplace(true);
    } else {
      onRun();
    }
  };

  const handleConfirmReplace = () => {
    setIsConfirmingReplace(false);
    onRun();
  };

  return (
    <>
      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend" focused={false} sx={{ fontSize: (theme) => theme.typography.size.small, fontWeight: 400, mb: 0.5 }}>
          Choose what to generate
        </FormLabel>
        <FormGroup sx={{ pl: 1 }}>
          <FormControlLabel
            label="Underground path and points"
            slotProps={{ typography: { variant: "body3" } }}
            control={
              <Checkbox
                size="small"
                sx={{ py: 0.25 }}
                checked={generate.underground}
                onChange={(event) => onGenerateChange({ ...generate, underground: event.target.checked })}
              />
            }
            disabled={readOnly}
          />
          <FormControlLabel
            label="Leadins"
            slotProps={{ typography: { variant: "body3" } }}
            control={
              <Checkbox
                size="small"
                sx={{ py: 0.25 }}
                checked={generate.leadins}
                onChange={(event) => onGenerateChange({ ...generate, leadins: event.target.checked })}
              />
            }
            disabled={readOnly}
          />
        </FormGroup>
        {/* The wrapper lets the tooltip show on a disabled button; an empty title renders no tooltip */}
        <Tooltip title={unmetRequirements.join(". ")}>
          <Box component="span" display="block">
            <Button fullWidth variant="contained" size="small" sx={{ mt: 1 }} disabled={isDisabled} onClick={handleRun}>
              Run data preparation
            </Button>
          </Box>
        </Tooltip>
      </FormControl>

      {isConfirmingReplace && replacesExistingData && (
        <ConfirmModal
          open
          disabled={isDisabled}
          header="Replace existing data?"
          confirmText="Continue"
          cancelText="Cancel"
          color="error"
          content={
            <Typography variant="body2">
              This will replace existing layer data ({replacedLayerNames.join(", ")}). This action cannot be undone.
            </Typography>
          }
          onConfirm={handleConfirmReplace}
          onCancel={() => setIsConfirmingReplace(false)}
        />
      )}
    </>
  );
};

export default RunDataPrep;
